import { type FormEvent, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Transfer4Fill from '@/assets/icons/transfer_4_fill.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';

import { MovedAtField } from './MovedAtField';

import styles from './BioscopeDetailMovingBioscopeModal.module.scss';

/**
 * The move form. A cleared choice is null (a null cage means onto land); a choice the bioscope
 * never had, such as the location of one on land, is undefined until the user picks one.
 */
export interface MovingState {
    bioscopeId: number;
    clientId: number;
    cageId: number | null | undefined;
    locationId: number | null | undefined;
    contractId: number | null | undefined;
    from: Date | null;
}

export interface MoveCallbackParameters {
    bioscopeId: number;
    cageId?: number | null;
    clientId: number;
    from: string;
    contractId?: number | null;
}

interface BioscopeDetailMovingBioscopeModalProps {
    initalState: MovingState;
    isModalOpenMoveBioscope: boolean;
    clients: {
        id: number;
        name: string;
        locations: {
            id: number;
            name: string;
            cages: {
                id: number;
                name: string;
                historical?: unknown;
            }[];
        }[];
        contract: {
            id: number;
            description: string;
        }[];
    }[];
    canModifyClient: boolean;
    canModifyContract: boolean;
    canModifyLocation: boolean;
    canMoveToLand: boolean;

    onCloseModal: () => void;
    onSubmit: (state: MoveCallbackParameters) => Promise<void>;
}

function BioscopeDetailMovingBioscopeModal({
    initalState,
    isModalOpenMoveBioscope,
    clients,
    onSubmit,
    onCloseModal,
    canModifyClient,
    canModifyContract,
    canModifyLocation,
    canMoveToLand,
}: BioscopeDetailMovingBioscopeModalProps) {
    const { t } = useTranslation();
    const [submitFailed, setSubmitFailed] = useState<ReactNode>(null);
    const [state, setState] = useState<MovingState>({ ...initalState });
    const locations = clients.find((client) => client.id === state.clientId)?.locations ?? [];
    const contracts = clients.find((client) => client.id === state.clientId)?.contract ?? [];
    const onClickMoveBioscope = async (ev: FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        if (state.from === null) {
            setSubmitFailed(t('molecules.bioscopeDetailMovingBioscopeModal.movedAtRequired'));
            return;
        }
        setSubmitFailed(null);
        try {
            await onSubmit({ ...state, from: state.from.toISOString() });
            onCloseModal();
        } catch (error: unknown) {
            setSubmitFailed(
                apiErrorContent(error, t('molecules.bioscopeDetailMovingBioscopeModal.moveFailed'))
            );
        }
    };

    function onChangeMovingState<T extends keyof MovingState, V = MovingState[T]>(key: T) {
        return (inputValue: V) => {
            const value = Number.isNaN(inputValue) ? null : inputValue;
            if (key === 'clientId') {
                setState((formData) => ({
                    ...formData,
                    locationId: null,
                    contractId: null,
                    cageId: null,
                }));
            }
            if (key === 'locationId') {
                setState((formData) => ({
                    ...formData,
                    cageId: null,
                }));
            }
            setState((formData) => ({ ...formData, [key]: value }));
        };
    }

    const availableCages =
        locations
            ?.find((item) => item.id === state.locationId)
            ?.cages?.filter((cage) => cage.historical === null)
            // sort, not toSorted: the build targets Chrome 97, and toSorted arrived in 110.
            .sort((a, b) => a.name.localeCompare(b.name)) ?? [];
    return (
        <ModalComponent
            isOpen={isModalOpenMoveBioscope}
            onClose={onCloseModal}
            centered
            title={`${t('common.actions.move')} ${t('common.labels.bioscope')} ${state.bioscopeId}`}
            titleIcon={Transfer4Fill}>
            <form onSubmit={onClickMoveBioscope}>
                <ModalBody>
                    {submitFailed && (
                        <Alert tone="danger" className={styles.submitError}>
                            {submitFailed}
                        </Alert>
                    )}
                    <FormField label={t('molecules.common.chooseClient')} htmlFor="client">
                        <Select
                            name="client"
                            id="client"
                            disabled={!canModifyClient}
                            value={state.clientId ?? ''}
                            onChange={(ev) =>
                                onChangeMovingState('clientId')(Number.parseInt(ev.target.value))
                            }>
                            <option key={0} value={''}>
                                {t('common.labels.nonExistentClient')}
                            </option>
                            {[...clients]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((client) => (
                                    <option key={client.id} value={client.id}>
                                        {client.name}
                                    </option>
                                ))}
                        </Select>
                    </FormField>
                    {state.clientId !== 0 && locations ? (
                        <>
                            <FormField
                                label={t(
                                    'molecules.bioscopeDetailMovingBioscopeModal.chooseLocation'
                                )}
                                htmlFor="location">
                                <Select
                                    key={state.clientId}
                                    name="location"
                                    id="location"
                                    disabled={!canModifyLocation}
                                    value={state.locationId ?? ''}
                                    onChange={(ev) =>
                                        onChangeMovingState('locationId')(
                                            Number.parseInt(ev.target.value)
                                        )
                                    }>
                                    {canMoveToLand && (
                                        <option key={0} value={''}>
                                            {t('common.labels.onLand')}
                                        </option>
                                    )}
                                    {[...locations]
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map((location) => (
                                            <option key={location.id} value={location.id}>
                                                {location.name}
                                            </option>
                                        ))}
                                </Select>
                            </FormField>

                            {Number.isInteger(state.locationId) && (
                                <FormField
                                    label={t(
                                        'molecules.bioscopeDetailMovingBioscopeModal.chooseCage'
                                    )}
                                    htmlFor="cage">
                                    <Select
                                        key={state.locationId}
                                        name="cage"
                                        id="cage"
                                        invalid={state.cageId === null}
                                        value={state.cageId ?? ''}
                                        onChange={(ev) =>
                                            onChangeMovingState('cageId')(
                                                Number.parseInt(ev.target.value)
                                            )
                                        }>
                                        {state.cageId == null && (
                                            <option key="cageIdNull" value={''}>
                                                --{' '}
                                                {t(
                                                    'molecules.bioscopeDetailMovingBioscopeModal.chooseCage'
                                                )}{' '}
                                                --
                                            </option>
                                        )}
                                        {availableCages.map((cage) => (
                                            <option key={cage.id} value={cage.id}>
                                                {cage.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormField>
                            )}
                            {canModifyContract && (
                                <FormField
                                    label={t(
                                        'molecules.bioscopeDetailMovingBioscopeModal.chooseContract'
                                    )}
                                    htmlFor="contract">
                                    <Select
                                        key={state.clientId}
                                        name="contract"
                                        id="contract"
                                        invalid={state.contractId == null}
                                        value={state.contractId ?? ''}
                                        onChange={(ev) =>
                                            onChangeMovingState('contractId')(
                                                Number.parseInt(ev.target.value)
                                            )
                                        }>
                                        {state.contractId == null && (
                                            <option key="cageIdNull" value={''}>
                                                --{' '}
                                                {t(
                                                    'molecules.bioscopeDetailMovingBioscopeModal.chooseContract'
                                                )}{' '}
                                                --
                                            </option>
                                        )}
                                        {contracts.map((contract) => {
                                            return (
                                                <option key={contract.id} value={contract.id}>
                                                    {contract.description}
                                                </option>
                                            );
                                        })}
                                    </Select>
                                </FormField>
                            )}
                            <MovedAtField
                                value={state.from}
                                onChange={onChangeMovingState('from')}
                            />
                        </>
                    ) : (
                        <></>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onCloseModal} variant="secondary">
                        {t('common.actions.cancel')}
                    </Button>
                    <Button type="submit">{t('common.actions.move')}</Button>
                </ModalFooter>
            </form>
        </ModalComponent>
    );
}

export default BioscopeDetailMovingBioscopeModal;
export { BioscopeDetailMovingBioscopeModal };
